/**
 * Passkey (WebAuthn) helpers.
 * - registerPasskey: enrolls the current device as a passkey for the signed-in user.
 * - signInWithPasskey: authenticates via a passkey and mints a fresh Supabase session.
 *
 * Passkeys are optional and additive — email/Google sign-in continues to work.
 */
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { supabase } from "@/integrations/supabase/client";

export function isPasskeySupported(): boolean {
  return typeof window !== "undefined" && browserSupportsWebAuthn();
}

export async function registerPasskey(deviceLabel?: string) {
  if (!isPasskeySupported()) throw new Error("Passkeys are not supported on this device");

  const { data: options, error: optErr } = await supabase.functions.invoke(
    "passkey-register",
    { body: { action: "options" } },
  );
  if (optErr || !options) throw new Error(optErr?.message || "Could not start registration");

  const attestation = await startRegistration({ optionsJSON: options });

  const { data: verifyRes, error: verifyErr } = await supabase.functions.invoke(
    "passkey-register",
    { body: { action: "verify", attestation, deviceLabel } },
  );
  if (verifyErr || !verifyRes?.verified) {
    throw new Error(verifyErr?.message || verifyRes?.error || "Registration failed");
  }
  return true;
}

export async function signInWithPasskey(email?: string) {
  if (!isPasskeySupported()) throw new Error("Passkeys are not supported on this device");

  const { data: options, error: optErr } = await supabase.functions.invoke(
    "passkey-authenticate",
    { body: { action: "options", email } },
  );
  if (optErr || !options) throw new Error(optErr?.message || "Could not start sign-in");

  const assertion = await startAuthentication({ optionsJSON: options });

  const { data: verifyRes, error: verifyErr } = await supabase.functions.invoke(
    "passkey-authenticate",
    { body: { action: "verify", assertion } },
  );
  if (verifyErr || !verifyRes?.verified) {
    throw new Error(verifyErr?.message || verifyRes?.error || "Sign-in failed");
  }

  const { error: sessionErr } = await supabase.auth.verifyOtp({
    email: verifyRes.email,
    token_hash: verifyRes.token_hash,
    type: "magiclink",
  });
  if (sessionErr) throw sessionErr;

  return true;
}
